import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { localCatalog } from '../data/catalog';
import { applyPageContent, applySiteSettings, localSite, siteIssues } from '../data/site';
import { demoFeatures, fetchCatalog, fetchPages, fetchSite, hasBackend, isAbortError } from '../utils/api';
import { applyFeatures } from '../data/features';
import { catalogIssues, createCatalogIndex, type CatalogIndex } from '../utils/catalog';
import { log } from '../core/log';
import { errorMessage, SERVER_ERROR_TEXT } from '../core/errors';
import { SiteProvider } from './SiteContext';
import type { Catalog } from '../core/types/catalog';
import type { SiteSettings } from '../core/types/site';
import type { PageContent } from '../core/types/pages';
import { pages as localPages } from '../data/pages';
type CatalogState = {
  status: 'loading';
} | {
  status: 'error';
  message: string;
} | {
  status: 'ready';
  catalog: Catalog;
};
const CatalogContext = createContext<CatalogIndex | null>(null);
/** Stil verversen (zonder laadscherm), bijv. na 409 `total_mismatch`. Zonder backend een no-op. */
const CatalogRefreshContext = createContext<() => Promise<void>>(async () => undefined);

/**
 * Hoe lang we op instellingen en paginateksten wachten voordat de winkel opent.
 * Ze zijn secundair: zonder antwoord tonen we de lokale teksten (data/site.ts, data/pages.ts)
 * in plaats van de webshop te laten wachten op het CMS.
 */
const SECONDARY_TIMEOUT_MS = 4000;

/**
 * Laadt de catalogus één keer en deelt hem met de hele site.
 * Zonder backend staat de lokale data direct klaar (geen laadscherm).
 *
 * Alleen de catalogus is kritiek. Instellingen en paginateksten mogen falen of traag zijn:
 * dan opent de winkel met de lokale teksten en komt er een waarschuwing in de log.
 * Vroeger brak `Promise.all` alles af zodra het CMS haperde, en zag de klant een foutscherm
 * terwijl de producten er gewoon waren.
 */
type ProviderProps = {
  children: ReactNode;
  /** Wat er staat zolang de catalogus laadt of niet lukt. Het scherm levert dit aan; de context kent geen schermen. */
  fallback: (error: string | null, retry: () => void) => ReactNode;
};
export function CatalogProvider({
  children,
  fallback
}: ProviderProps) {
  const [state, setState] = useState<CatalogState>(() => {
    if (hasBackend) return {
      status: 'loading'
    };
    // Demo: geen GET /site, maar wel de onderdelen zoals het beheer ze opsloeg (vóór het eerste tekenen).
    if (demoFeatures) applyFeatures(demoFeatures());
    return {
      status: 'ready',
      catalog: localCatalog
    };
  });
  // De instellingen als state (docs/ARCHITECTUUR.md §6): na elke `applySiteSettings` een nieuwe stand.
  const [site, setSite] = useState<SiteSettings>(localSite);
  // Instellingen en teksten zijn secundair: een onverwachte vorm mag de winkel nooit tegenhouden.
  const applySite = useCallback((next: SiteSettings) => {
    try {
      applySiteSettings(next);
    } catch (err) {
      log.warn('catalogus', 'Instellingen hadden een onverwachte vorm; lokale waarden gebruikt.', [err instanceof Error ? err.message : String(err)]);
    }
    // Uit de bijgewerkte gedeelde objecten: samengevoegd met de lokale standaard en de staffel gesorteerd.
    setSite(localSite());
  }, []);
  // Paginateksten idem: `applyPageContent` vult de gedeelde teksten aan (oudere opslag krijgt de
  // standaard erbij); daarna een nieuwe stand, zodat wie `usePages()` leest opnieuw tekent.
  const [pageTexts, setPageTexts] = useState<PageContent>(() => ({
    ...localPages
  }));
  const applyPages = useCallback((next: PageContent) => {
    try {
      applyPageContent(next);
    } catch (err) {
      log.warn('catalogus', 'Paginateksten hadden een onverwachte vorm; lokale teksten gebruikt.', [err instanceof Error ? err.message : String(err)]);
    }
    setPageTexts({
      ...localPages
    });
  }, []);

  // Opnieuw proberen kan dubbel afgaan (knop én het 'online'-event): een oudere poging
  // wordt afgebroken, zodat zijn antwoord nooit over een nieuwere heen komt.
  const inFlight = useRef<AbortController | null>(null);
  const load = useCallback(async () => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    const {
      signal
    } = controller;
    setState({
      status: 'loading'
    });

    /** Een secundair verzoek: faalt of verloopt het, dan `null` (= lokale data) en nooit een fout. */
    const optional = async <T,>(name: string, run: () => Promise<T | null>): Promise<T | null> => {
      try {
        return await run();
      } catch (err) {
        if (!signal.aborted) {
          log.warn('catalogus', `${name} niet geladen, lokale data gebruikt.`, [err instanceof Error ? err.message : String(err)]);
        }
        return null;
      }
    };

    // Alle drie tegelijk starten; alleen de catalogus mag de winkel tegenhouden.
    const site = optional('Instellingen', () => fetchSite({
      signal,
      timeoutMs: SECONDARY_TIMEOUT_MS
    }));
    const pages = optional('Paginateksten', () => fetchPages({
      signal,
      timeoutMs: SECONDARY_TIMEOUT_MS
    }));
    try {
      const catalog = await fetchCatalog({
        signal
      });
      // Instellingen gaan vóór het eerste tekenen in (prijzen, verzendgrens); de time-out houdt dit kort.
      const [siteSettings, pageContent] = await Promise.all([site, pages]);
      if (inFlight.current !== controller) return;
      inFlight.current = null;
      if (siteSettings) applySite(siteSettings);
      if (pageContent) applyPages(pageContent);
      setState({
        status: 'ready',
        catalog
      });
    } catch (err) {
      // Zonder catalogus geen winkel: de secundaire verzoeken hebben dan geen zin meer.
      controller.abort();
      if (inFlight.current !== controller || isAbortError(err)) return;
      inFlight.current = null;
      // Nooit de ruwe servertekst (5xx kan technisch zijn); errorMessage kent de vaste teksten.
      setState({
        status: 'error',
        message: errorMessage(err) ?? SERVER_ERROR_TEXT
      });
    }
  }, [applySite, applyPages]);
  useEffect(() => {
    if (hasBackend) void load();
    return () => inFlight.current?.abort();
  }, [load]);

  /**
   * Prijzen, voorraad en instellingen opnieuw ophalen terwijl de winkel blijft staan.
   * Na een afgewezen totaal moet de mand rekenen met wat de server nu rekent, anders
   * stuurt elke nieuwe poging hetzelfde (foute) bedrag. Mislukt het, dan blijft de huidige stand.
   */
  const refreshTicket = useRef(0);
  const refresh = useCallback(async () => {
    if (!hasBackend || inFlight.current) return;
    // Twee keer kort na elkaar verversen: alleen het laatste antwoord telt.
    const ticket = ++refreshTicket.current;
    try {
      const [catalog, siteSettings] = await Promise.all([fetchCatalog(), fetchSite({
        timeoutMs: SECONDARY_TIMEOUT_MS
      }).catch(() => null)]);
      if (inFlight.current || ticket !== refreshTicket.current) return;
      if (siteSettings) applySite(siteSettings);
      setState({
        status: 'ready',
        catalog
      });
    } catch (err) {
      log.warn('catalogus', 'Verversen lukte niet, huidige prijzen blijven staan.', [err instanceof Error ? err.message : String(err)]);
    }
  }, [applySite]);
  const index = useMemo(() => state.status === 'ready' ? createCatalogIndex(state.catalog) : null, [state]);
  useEffect(() => {
    if (!index) return;
    const issues = [...catalogIssues(index), ...siteIssues(site, id => Boolean(index.productById(id)))];
    if (issues.length) log.warn('catalogus', 'Koppelingen kloppen niet:', issues);
  }, [index, site]);
  if (!index) {
    return <>{fallback(state.status === 'error' ? state.message : null, load)}</>;
  }
  return <SiteProvider site={site} pages={pageTexts} data-id="element-5521" data-mp="5wqbjjr4qu9g">
      <CatalogRefreshContext.Provider value={refresh} data-id="element-5522" data-mp="at6w63z4eqhs">
        <CatalogContext.Provider value={index} data-id="element-5523" data-mp="aw2wfyyfxr9e">{children}</CatalogContext.Provider>
      </CatalogRefreshContext.Provider>
    </SiteProvider>;
}
export function useCatalogRefresh() {
  return useContext(CatalogRefreshContext);
}
export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCatalog moet binnen <CatalogProvider> gebruikt worden');
  return ctx;
}